import React, { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import { badgeClass, DetailCard, DetailItem, formatNumber, getApiErrorMessage, inputClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Source = 'purchase' | 'production';

interface Batch {
  id: string;
  productName: string;
  sku: string;
  unit: string;
  batchNumber: string;
  heatNumber: string | null;
  millTcNumber: string | null;
  source: Source;
  vendorName: string | null;
  workOrderNumber: string | null;
  quantity: number;
  availableQuantity: number;
  firstReceivedOn: string;
}
interface BatchPage {
  data: Batch[];
  total: number;
  totalPages: number;
}
interface Summary {
  batches: number;
  batchesInStock: number;
  purchasedBatches: number;
  producedBatches: number;
  heatNumbers: number;
}
interface MadeFrom {
  batch: Batch;
  quantity: number;
  madeFrom: MadeFrom[];
}
interface Dispatch {
  challanNumber: string;
  dispatchDate: string;
  orderNumber: string | null;
  customerName: string | null;
  quantity: number;
}
interface UsedIn {
  workOrderNumber: string;
  quantity: number;
  produced: { batch: Batch; dispatches: Dispatch[]; usedIn: UsedIn[] } | null;
}
interface Trace {
  batch: Batch;
  receipts: { grnNumber: string; receiptDate: string; purchaseOrderNumber: string | null; vendorName: string | null; vendorChallanNumber: string | null; vendorInvoiceNumber: string | null; quantity: number }[];
  madeFrom: MadeFrom[];
  usedIn: UsedIn[];
  dispatches: Dispatch[];
  movements: { id: string; kind: string; quantity: number; movementDate: string; referenceNumber: string; reversed: boolean; reversalNote: string | null }[];
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const SOURCE: Record<Source, { label: string; className: string }> = {
  purchase: { label: 'Purchased', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  production: { label: 'Made here', className: 'bg-purple-50 text-purple-700 ring-purple-600/20' },
};
const KIND: Record<string, string> = { receipt: 'Received', production: 'Made', consumption: 'Used on work order', dispatch: 'Dispatched' };

const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));

const batchApi = {
  page: async (params: object): Promise<BatchPage> => (await api.get('/batches', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/batches/summary')).data,
  trace: async (id: string): Promise<Trace> => (await api.get(`/batches/${id}/trace`)).data,
};

// ---------------------------------------------------------------- the trace of one batch

const BatchLine: React.FC<{ batch: Batch; quantity?: number; onOpen: (id: string) => void }> = ({ batch, quantity, onOpen }) => (
  <p className="text-sm text-gray-900">
    <button type="button" onClick={() => onOpen(batch.id)} className="font-semibold text-indigo-600 hover:text-indigo-500">
      {batch.batchNumber}
    </button>{' '}
    <span className="text-gray-700">{batch.productName}</span>
    {quantity !== undefined && (
      <span className="text-gray-900 tabular-nums">
        {' '}
        · {formatNumber(quantity)} {batch.unit}
      </span>
    )}
    <span className="block text-xs text-gray-500">
      {[
        batch.heatNumber ? `Heat ${batch.heatNumber}` : null,
        batch.millTcNumber ? `Mill TC ${batch.millTcNumber}` : null,
        batch.source === 'purchase' ? batch.vendorName : batch.workOrderNumber,
      ]
        .filter(Boolean)
        .join(' · ') || '—'}
    </span>
  </p>
);

const DispatchList: React.FC<{ dispatches: Dispatch[]; unit: string }> = ({ dispatches, unit }) => (
  <ul className="space-y-1">
    {dispatches.map((dispatch) => (
      <li key={dispatch.challanNumber} className="text-sm text-gray-900">
        <span className="font-medium">{dispatch.challanNumber}</span> · {dispatch.customerName ?? 'Customer'}
        <span className="tabular-nums">
          {' '}
          · {formatNumber(dispatch.quantity)} {unit}
        </span>
        <span className="block text-xs text-gray-500">
          {formatDate(dispatch.dispatchDate)}
          {dispatch.orderNumber ? ` · ${dispatch.orderNumber}` : ''}
        </span>
      </li>
    ))}
  </ul>
);

const MadeFromTree: React.FC<{ items: MadeFrom[]; onOpen: (id: string) => void }> = ({ items, onOpen }) => (
  <ul className="space-y-2">
    {items.map((item) => (
      <li key={item.batch.id}>
        <BatchLine batch={item.batch} quantity={item.quantity} onOpen={onOpen} />
        {item.madeFrom.length > 0 && (
          <div className="ml-3 mt-2 border-l-2 border-gray-200 pl-3">
            <MadeFromTree items={item.madeFrom} onOpen={onOpen} />
          </div>
        )}
      </li>
    ))}
  </ul>
);

const UsedInTree: React.FC<{ items: UsedIn[]; unit: string; onOpen: (id: string) => void }> = ({ items, unit, onOpen }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item.workOrderNumber}>
        <p className="text-sm text-gray-900">
          <span className="font-medium">{item.workOrderNumber}</span>
          <span className="tabular-nums">
            {' '}
            used {formatNumber(item.quantity)} {unit}
          </span>
        </p>
        {item.produced && (
          <div className="ml-3 mt-1 space-y-2 border-l-2 border-gray-200 pl-3">
            <BatchLine batch={item.produced.batch} quantity={item.produced.batch.quantity} onOpen={onOpen} />
            {item.produced.dispatches.length > 0 && (
              <div className="ml-3 border-l-2 border-green-200 pl-3">
                <DispatchList dispatches={item.produced.dispatches} unit={item.produced.batch.unit} />
              </div>
            )}
            {item.produced.usedIn.length > 0 && <UsedInTree items={item.produced.usedIn} unit={item.produced.batch.unit} onOpen={onOpen} />}
          </div>
        )}
      </li>
    ))}
  </ul>
);

const Section: React.FC<{ title: string; empty: string; isEmpty: boolean; children: React.ReactNode }> = ({ title, empty, isEmpty, children }) => (
  <section className="rounded-lg border border-gray-200">
    <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
    <div className="px-4 py-3">{isEmpty ? <p className="text-sm text-gray-500">{empty}</p> : children}</div>
  </section>
);

const TracePanel: React.FC<{ batchId: string; onOpen: (id: string) => void; onClose: () => void }> = ({ batchId, onOpen, onClose }) => {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['batches', 'trace', batchId], queryFn: () => batchApi.trace(batchId) });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !data) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The batch could not be loaded.')}</p>;
  const { batch } = data;

  return (
    <div className="space-y-4">
      <DetailCard title={`${batch.productName} · ${batch.sku}`}>
        <DetailItem label="Heat no.">{batch.heatNumber || '—'}</DetailItem>
        <DetailItem label="Mill TC">{batch.millTcNumber || '—'}</DetailItem>
        <DetailItem label={batch.source === 'purchase' ? 'Vendor' : 'Work order'}>{(batch.source === 'purchase' ? batch.vendorName : batch.workOrderNumber) || '—'}</DetailItem>
        <DetailItem label="Left of it">
          {formatNumber(batch.availableQuantity)} of {formatNumber(batch.quantity)} {batch.unit}
        </DetailItem>
      </DetailCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section
            title="Where it came from"
            isEmpty={data.receipts.length === 0 && data.madeFrom.length === 0}
            empty={batch.source === 'production' ? `Made on ${batch.workOrderNumber ?? 'a work order'}; no material batches were recorded for it.` : 'No goods receipt is recorded.'}
          >
            {data.receipts.length > 0 && (
              <ul className="space-y-1">
                {data.receipts.map((receipt) => (
                  <li key={`${receipt.grnNumber}-${receipt.quantity}`} className="text-sm text-gray-900">
                    <span className="font-medium">{receipt.grnNumber}</span> · {receipt.vendorName ?? 'Vendor'}
                    <span className="tabular-nums">
                      {' '}
                      · {formatNumber(receipt.quantity)} {batch.unit}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {[formatDate(receipt.receiptDate), receipt.purchaseOrderNumber, receipt.vendorChallanNumber ? `challan ${receipt.vendorChallanNumber}` : null, receipt.vendorInvoiceNumber ? `invoice ${receipt.vendorInvoiceNumber}` : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {data.madeFrom.length > 0 && (
              <div>
                <p className="mb-2 text-xs text-gray-500">Made on {batch.workOrderNumber} from:</p>
                <MadeFromTree items={data.madeFrom} onOpen={onOpen} />
              </div>
            )}
          </Section>
        </div>
        <div className="space-y-4">
          <Section title="Where it went" isEmpty={data.usedIn.length === 0 && data.dispatches.length === 0} empty="Nothing of this batch has been used or dispatched yet.">
            <div className="space-y-3">
              {data.dispatches.length > 0 && <DispatchList dispatches={data.dispatches} unit={batch.unit} />}
              {data.usedIn.length > 0 && <UsedInTree items={data.usedIn} unit={batch.unit} onOpen={onOpen} />}
            </div>
          </Section>
        </div>
      </div>

      <Section title={`Movements · ${formatNumber(data.movements.length)}`} isEmpty={data.movements.length === 0} empty="No movements.">
        <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto">
          {data.movements.map((movement) => (
            <li key={movement.id} className={cn('flex items-center justify-between gap-3 py-1.5 text-sm', movement.reversed && 'text-gray-400 line-through')}>
              <span>
                {formatDate(movement.movementDate)} · {KIND[movement.kind] ?? movement.kind} · {movement.referenceNumber}
                {movement.reversed && <span className="ml-2 no-underline">(taken back)</span>}
              </span>
              <span className={cn('tabular-nums', !movement.reversed && (movement.quantity > 0 ? 'text-green-700' : 'text-gray-900'))}>
                {movement.quantity > 0 ? '+' : ''}
                {formatNumber(movement.quantity)}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <div className="flex justify-end">
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Batches: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<Source | ''>('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['batches', 'page', { page, limit, search, source: sourceFilter, inStockOnly }],
    queryFn: () => batchApi.page({ page, limit, search, source: sourceFilter, inStockOnly }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['batches', 'summary'], queryFn: batchApi.summary });
  // The batch in the heading of the trace window; a trace can walk to a batch that is not on this page
  const { data: viewing } = useQuery({ queryKey: ['batches', 'trace', viewingId], queryFn: () => batchApi.trace(viewingId as string), enabled: !!viewingId });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!sourceFilter || inStockOnly;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  const stats = [
    { label: 'Batches', value: formatNumber(summary?.batches ?? 0), hint: `${formatNumber(summary?.batchesInStock ?? 0)} still have stock` },
    { label: 'Purchased', value: formatNumber(summary?.purchasedBatches ?? 0), hint: 'From goods receipts' },
    { label: 'Made here', value: formatNumber(summary?.producedBatches ?? 0), hint: 'From work orders' },
    { label: 'Heat numbers', value: formatNumber(summary?.heatNumbers ?? 0), hint: 'Different heats on record' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Batches &amp; Heat Numbers</h1>
        <p className="mt-1 text-sm text-gray-500">Every batch received or made, and its trace: back to the vendor, heat number and mill test certificate, forward to the work orders and customers it went to.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by batch, heat or mill TC number, product, vendor or work order" aria-label="Search batches" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={sourceFilter} onChange={(event) => { setSourceFilter(event.target.value as Source | ''); setPage(1); }} aria-label="Source" className={cn(inputClass, 'lg:w-44')}>
            <option value="">Purchased and made</option>
            {(Object.keys(SOURCE) as Source[]).map((source) => (<option key={source} value={source}>{SOURCE[source].label}</option>))}
          </select>
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-700">
            <input type="checkbox" checked={inStockOnly} onChange={(event) => { setInStockOnly(event.target.checked); setPage(1); }} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            In stock only
          </label>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Batch</th>
                <th scope="col" className={thClass}>Product</th>
                <th scope="col" className={thClass}>Heat no. / Mill TC</th>
                <th scope="col" className={thClass}>From</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Left</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The batches could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No batch matches' : 'No batches yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">A batch starts when goods are received with a batch or heat number, and when a work order is completed.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.batchNumber}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{formatDate(row.firstReceivedOn)}</p>
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <p className="truncate text-sm text-gray-900">{row.productName}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{row.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.heatNumber || <span className="text-gray-400">—</span>}
                      {row.millTcNumber && <span className="block text-xs text-gray-500">TC {row.millTcNumber}</span>}
                    </td>
                    <td className="max-w-xs px-4 py-3">
                      <span className={cn(badgeClass, SOURCE[row.source].className)}>{SOURCE[row.source].label}</span>
                      <p className="mt-0.5 truncate text-xs text-gray-500">{(row.source === 'purchase' ? row.vendorName : row.workOrderNumber) || '—'}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(row.quantity)} <span className="text-gray-500">{row.unit}</span>
                    </td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', row.availableQuantity > 0 ? 'font-medium text-gray-900' : 'text-gray-400')}>{formatNumber(row.availableQuantity)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Trace batch ${row.batchNumber}`}>
                        Trace
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={!!viewingId} onClose={() => setViewingId(null)} title={viewing ? `Batch ${viewing.batch.batchNumber}` : 'Batch'} size="2xl">
        {viewingId && <TracePanel key={viewingId} batchId={viewingId} onOpen={setViewingId} onClose={() => setViewingId(null)} />}
      </Modal>
    </div>
  );
};

export default Batches;
